import {
  AloftPrice,
  SalesComparisonApproachType,
  applyRoundingAndThresholds,
  isUndefined,
} from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import { selectManualCosts } from '../costSelectors/selectManualCost.ts';
import {
  selectAdjustmentSettings,
  selectCompTypes,
  selectSalesComparisonApproach,
} from '../orderSelectors.ts';
import { selectImprovementEntityIds } from '../propertyDataSelectors/improvementsSelectors.ts';
import { selectManualAdjustmentsEntities } from '../propertyDataSelectors/manualAdjustmentsSelectors.ts';
import { selectDepreciationRatiosForFeature } from '../selectDepreciationRatioForFeature.ts';
import { createPriceSelector } from '../utils/createPriceSelector.ts';
import { getColorFromAdjustment } from './utils/getColorFromAdjustment.ts';
import { getStringValueFromAdjustment } from './utils/getStringValueFromAdjustment.ts';

const calculateManualCostAdj = (
  manualCosts: ReturnType<typeof selectManualCosts>,
  depreciationRatios: ReturnType<typeof selectDepreciationRatiosForFeature>,
  compID: string,
  compTypes: ReturnType<typeof selectCompTypes>,
  salesComparisonApproach: SalesComparisonApproachType,
) => {
  // No unit-rate adjustments for manual cost
  if (salesComparisonApproach === 'unit_rate') return undefined;

  let subjectManualCost = manualCosts.subject;
  if (compTypes[compID] === 'arv') subjectManualCost = manualCosts.subjectArv;

  const compManualCost = manualCosts[compID];

  if (!depreciationRatios) return undefined;
  const depreciationRatio = depreciationRatios.subject;
  const compDepreciationRatio = depreciationRatios[compID];

  if (isUndefined(depreciationRatio) || isUndefined(compDepreciationRatio)) return undefined;

  const subjectDepreciatedManualCost =
    subjectManualCost?.mul(1 - depreciationRatio) ?? AloftPrice.zero;
  const compDepreciatedManualCost =
    compManualCost?.mul(1 - compDepreciationRatio) ?? AloftPrice.zero;

  return subjectDepreciatedManualCost.sub(compDepreciatedManualCost);
};

const selectManualCostAdj = createPriceSelector(
  [
    selectManualCosts,
    (state) => selectDepreciationRatiosForFeature(state, 'manualCost'),
    (state, id: string) => id,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  calculateManualCostAdj,
);

export const selectManualCostAdjs = createSelector(
  [
    selectManualCosts,
    (state) => selectDepreciationRatiosForFeature(state, 'manualCost'),
    selectImprovementEntityIds,
    selectCompTypes,
    selectSalesComparisonApproach,
  ],
  (manualCosts, depreciationRatios, improvementEntityIds, compTypes, salesComparisonApproach) =>
    improvementEntityIds.reduce((acc: Record<string, AloftPrice>, propID) => {
      const adj = calculateManualCostAdj(
        manualCosts,
        depreciationRatios,
        propID,
        compTypes,
        salesComparisonApproach,
      );

      if (adj) acc[propID] = adj;
      return acc;
    }, {}),
);

const selectAppliedManualCostAdj = createPriceSelector(
  [selectManualCostAdj, selectAdjustmentSettings],
  (adj, adjSettings) => applyRoundingAndThresholds(adj, 'manual', adjSettings),
);

export const selectAppliedManualCostAdjDisplay = createSelector(
  [selectAppliedManualCostAdj],
  (adj) => {
    if (!adj) return undefined;

    return {
      value: getStringValueFromAdjustment(adj),
      color: getColorFromAdjustment(adj),
    };
  },
);

export const selectAppliedManualCostAdjsDisplay = createSelector(
  [selectManualCostAdjs, selectAdjustmentSettings, selectManualAdjustmentsEntities],
  (adjs, adjSettings, manualAdjustmentEntities) =>
    Object.keys(adjs).reduce((acc: Record<string, { value: string; color: string }>, propID) => {
      const adj = adjs[propID];
      const manualAdjustment = manualAdjustmentEntities[propID]?.manual;

      if (manualAdjustment) {
        const manualAdjustmentPrice = AloftPrice.schema.parse(manualAdjustment);
        acc[propID] = {
          value: `${getStringValueFromAdjustment(manualAdjustmentPrice)}*`,
          color: getColorFromAdjustment(manualAdjustmentPrice),
        };
      } else if (adj) {
        acc[propID] = {
          value: getStringValueFromAdjustment(
            applyRoundingAndThresholds(adj, 'manual', adjSettings),
          ),
          color: getColorFromAdjustment(applyRoundingAndThresholds(adj, 'manual', adjSettings)),
        };
      }

      return acc;
    }, {}),
);
